import {type Insets, Platform} from 'react-native'
import {type AppBskyActorDefs} from '@atproto/api'

import {type ProxyHeaderValue} from '#/state/session/agent'
import {APP_LABELER_DID, BLUESKY_PROXY_DID, CHAT_PROXY_DID} from '#/env'

type EnvTypes = 'dev' | 'stage' | 'prod'

export const APP_ENV: EnvTypes = (process.env.ENVIRONMENT as EnvTypes) ?? 'dev'

export const IS_DEV = APP_ENV === 'dev'
export const IS_STAGE = APP_ENV === 'stage'
export const IS_PROD = APP_ENV === 'prod'

export const BSKY_APP_ACCOUNT_DID =
  process.env.EXPO_PUBLIC_APP_ACCOUNT_DID ?? 'did:plc:z72i7hdynmk6r22z27h6tvur'

/*const WSOC_DISCOVER_PUBLISHER_DID_BY_ENV: Record<EnvTypes, string> = {
  dev: 'did:plc:y5p7e6itcmpmnzitgszba4rg', // wow.pds-dev.wsocial.dev
  stage: 'did:plc:f4hpdzclylxt2ea5yjo4au3f', // wow.pds-stage.wsocial.dev
  prod: 'did:plc:nkhofy62y35lz27mgu3yilnq', // wow.wsocial.eu
}*/
const WSOC_DISCOVER_PUBLISHER_DID = BSKY_APP_ACCOUNT_DID //WSOC_DISCOVER_PUBLISHER_DID_BY_ENV[APP_ENV]

export const PUBLIC_API_URL = process.env.PUBLIC_API_URL ?? 'https://wsocial.eu'
export const APP_API_URL =
  process.env.APP_API_URL ?? 'https://pds-dev.wsocial.dev'
export const APP_PUBLIC_API_URL = APP_API_URL
// process.env.APP_PUBLIC_API_URL ?? 'https://public.api.bsky.app'
const PUBLIC_APP_URL = process.env.PUBLIC_APP_URL ?? 'https://dev.wsocial.dev'
const EMBED_APP_URL =
  process.env.EMBED_APP_URL ?? 'https://embed-dev.wsocial.dev'
export const NEXTJS_APP_URL =
  process.env.EXPO_PUBLIC_NEXTJS_APP_URL ?? `${PUBLIC_APP_URL}/public`

export const DISCOVER_FEED_URL =
  process.env.EXPO_PUBLIC_DISCOVER_FEED_URL ||
  `/profile/${WSOC_DISCOVER_PUBLISHER_DID}/feed/whats-hot`

export const LOCAL_DEV_SERVICE =
  Platform.OS === 'android' ? 'http://10.0.2.2:2583' : 'http://localhost:2583'
export const STAGING_SERVICE = 'https://staging.bsky.dev'
export const BSKY_SERVICE = APP_API_URL ?? 'https://bsky.social'
export const BSKY_SERVICE_DID = 'did:web:bsky.social'
export const PUBLIC_BSKY_SERVICE = APP_PUBLIC_API_URL
export const DEFAULT_SERVICE = BSKY_SERVICE
const HELP_DESK_LANG = 'en-us'
export const HELP_DESK_URL = `https://blueskyweb.zendesk.com/hc/${HELP_DESK_LANG}`
export const WSOCIAL_LIVE_SERVER = 'https://live.wsocial.eu'

/**
 * Origins allowed to embed the `/session-broker` route and receive the current
 * session over postMessage (see `src/screens/SessionBrokerScreen`). Keep in
 * sync with the `frame-ancestors` allowlist for that path in `nginx.conf`.
 */
const SESSION_BROKER_ORIGINS_RAW: string =
  process.env.EXPO_PUBLIC_SESSION_BROKER_ORIGINS ?? 'https://live.wsocial.eu'
export const SESSION_BROKER_ALLOWED_ORIGINS: string[] =
  SESSION_BROKER_ORIGINS_RAW.split(',')
    .map(origin => origin.trim())
    .filter(Boolean)

export const EMBED_SERVICE = EMBED_APP_URL
export const EMBED_SCRIPT = `${EMBED_APP_URL}/public/assets/js/embed.js`
export const BSKY_DOWNLOAD_URL = `${PUBLIC_APP_URL}/download`
export const STARTER_PACK_MAX_SIZE = 150
export const CARD_ASPECT_RATIO = 1200 / 630

// HACK
// Yes, this is exactly what it looks like. It's a hard-coded constant
// reflecting the number of new users in the last week. We don't have
// time to add a route to the servers for this so we're just going to hard
// code and update this number with each release until we can get the
// server route done.
// -prf
export const JOINED_THIS_WEEK = 560000 // estimate as of 12/18/24

export const DISCOVER_DEBUG_DIDS: Record<string, true> = {
  /*'did:plc:oisofpd7lj26yvgiivf3lxsi': true, // hailey.at
  'did:plc:p2cp5gopk7mgjegy6wadk3ep': true, // samuel.bsky.team
  'did:plc:ragtjsm2j2vknwkz3zp4oxrd': true, // pfrazee.com
  'did:plc:vpkhqolt662uhesyj6nxm7ys': true, // why.bsky.team
  'did:plc:3jpt2mvvsumj2r7eqk4gzzjz': true, // esb.lol
  'did:plc:vjug55kidv6sye7ykr5faxxn': true, // emilyliu.me
  'did:plc:tgqseeot47ymot4zro244fj3': true, // iwsmith.bsky.social
  'did:plc:2dzyut5lxna5ljiaasgeuffz': true, // darrin.bsky.team*/
}

const _BASE_FEEDBACK_FORM_URL = `${HELP_DESK_URL}/requests/new`
export function FEEDBACK_FORM_URL({
  email: _email,
  handle,
}: {
  email?: string
  handle?: string
}): string {
  const to = 'support@wsocial.eu'
  const subject = `Feedback from ${handle ? encodeURIComponent(handle) : 'anonymous'}`
  /*let str = BASE_FEEDBACK_FORM_URL
  if (email) {
    str += `?tf_anonymous_requester_email=${encodeURIComponent(email)}`
    if (handle) {
      str += `&tf_17205412673421=${encodeURIComponent(handle)}`
    }
  }*/
  return `mailto:${to}?subject=${subject}`
}

export const MAX_DISPLAY_NAME = 64
export const MAX_DESCRIPTION = 256

export const MAX_GRAPHEME_LENGTH = 300

export const MAX_DRAFT_GRAPHEME_LENGTH = 1000

export const MAX_DM_GRAPHEME_LENGTH = 1000

export const MAX_GROUP_NAME_GRAPHEME_LENGTH = 50

// Recommended is 100 per: https://www.w3.org/WAI/GL/WCAG20/tests/test3.html
// but increasing limit per user feedback
export const MAX_ALT_TEXT = 2000

export const MAX_REPORT_REASON_GRAPHEME_LENGTH = 2000

export function IS_TEST_USER(handle?: string) {
  return handle && handle?.endsWith('.test')
}

export function IS_PROD_SERVICE(url?: string) {
  return url && url !== STAGING_SERVICE && !url.startsWith(LOCAL_DEV_SERVICE)
}

export const PROD_DEFAULT_FEED = (rkey: string) =>
  `at://${WSOC_DISCOVER_PUBLISHER_DID}/app.bsky.feed.generator/${rkey}`

export const STAGING_DEFAULT_FEED = (rkey: string) =>
  `at://did:plc:yofh3kx63drvfljkibw5zuxo/app.bsky.feed.generator/${rkey}`

export const PROD_FEEDS = [
  `feedgen|${PROD_DEFAULT_FEED('whats-hot')}`,
  `feedgen|${PROD_DEFAULT_FEED('thevids')}`,
]

export const STAGING_FEEDS = [
  `feedgen|${STAGING_DEFAULT_FEED('whats-hot')}`,
  `feedgen|${STAGING_DEFAULT_FEED('thevids')}`,
]

export const IMAGE_SIZE_CONFIG_POSTS = {
  maxDimension: 4000,
  maxSize: 2000000,
}

export const IMAGE_SIZE_CONFIG_2K_1MB = {
  maxDimension: 2000,
  maxSize: 1000000,
}

export const STAGING_LINK_META_PROXY =
  'https://cardyb.staging.bsky.dev/v1/extract?url='

export const PROD_LINK_META_PROXY = 'https://cardyb.bsky.app/v1/extract?url='

export function LINK_META_PROXY(_serviceUrl: string) {
  if (IS_DEV) {
    return STAGING_LINK_META_PROXY
  }

  return PROD_LINK_META_PROXY
}

export const STATUS_PAGE_URL = 'https://status.bsky.app/'

// Hitslop constants
export const createHitslop = (size: number): Insets => ({
  top: size,
  left: size,
  bottom: size,
  right: size,
})
export const HITSLOP_10 = createHitslop(10)
export const HITSLOP_20 = createHitslop(20)
export const HITSLOP_30 = createHitslop(30)
export const LANG_DROPDOWN_HITSLOP = {top: 10, bottom: 10, left: 4, right: 4}
export const BACK_HITSLOP = HITSLOP_30
export const MAX_POST_LINES = 25

export const BSKY_FEED_OWNER_DIDS = [
  WSOC_DISCOVER_PUBLISHER_DID,
  'did:plc:vpkhqolt662uhesyj6nxm7ys',
  'did:plc:q6gjnaw2blty4crticxkmujt',
]

export const TRENDING_DID = 'did:plc:qrz3lhbyuxbeilrc6nekdqme'
export const TRENDING_HANDLE = 'trending.bsky.app'

// "Discover" feed pinned for new users in `screens/Onboarding/StepFinished`.
// The publisher DID is selected by `APP_ENV` via
// `WSOC_DISCOVER_PUBLISHER_DID_BY_ENV` (top of this file) so dev / stage /
// prod each point at their own publisher. Each env's AppView only indexes
// records on its own PDS, so cross-env URIs return broken feeds.
//
// Build-time escape hatch:
//   EXPO_PUBLIC_DISCOVER_FEED_URI=at://<publisher-did>/app.bsky.feed.generator/<rkey>
//
// IMPORTANT: this only affects feeds we *write* (e.g. the StepFinished
// pin and the NoFeedsPinned reset). Existing users keep whatever value is
// already in their PDS `savedFeedsPrefV2` — they need to manually re-pin
// or be migrated. New signups get this URI automatically.
//
// Build-time, not runtime: same caveat as EXPO_PUBLIC_APP_ACCOUNT_DID —
// bake it into the image during `yarn build-web`, not via the Pod env.
export const DISCOVER_FEED_URI =
  process.env.EXPO_PUBLIC_DISCOVER_FEED_URI ??
  `at://${WSOC_DISCOVER_PUBLISHER_DID}/app.bsky.feed.generator/discover`
export const VIDEO_FEED_URI = PROD_DEFAULT_FEED('thevids')

export const STAGING_VIDEO_FEED_URI =
  'at://did:plc:yofh3kx63drvfljkibw5zuxo/app.bsky.feed.generator/thevids'

export const LIST_TO_PIN_URI =
  process.env.EXPO_PUBLIC_LIST_TO_PIN_URI ?? undefined
// Ingmar's list
//'at://did:plc:lhl3qusfghb3hctfosys34kv/app.bsky.graph.list/3mgzj6s6zxs2m'

export const VIDEO_FEED_URIS = [VIDEO_FEED_URI, STAGING_VIDEO_FEED_URI]
export const DISCOVER_SAVED_FEED = {
  type: 'feed',
  value: DISCOVER_FEED_URI,
  pinned: true,
}
export const TIMELINE_SAVED_FEED = {
  type: 'timeline',
  value: 'following',
  pinned: true,
}
export const VIDEO_SAVED_FEED = {
  type: 'feed',
  value: VIDEO_FEED_URI,
  pinned: true,
}

export const DEFAULT_SAVED_LISTS = [
  {
    type: 'list',
    value: LIST_TO_PIN_URI,
    pinned: true,
  },
].filter(item => item.value !== undefined)

export const RECOMMENDED_SAVED_FEEDS: Pick<
  AppBskyActorDefs.SavedFeed,
  'type' | 'value' | 'pinned'
>[] = [DISCOVER_SAVED_FEED, TIMELINE_SAVED_FEED]

export const KNOWN_SHUTDOWN_FEEDS = [
  'at://did:plc:wqowuobffl66jv3kpsvo7ak4/app.bsky.feed.generator/the-algorithm', // for you by skygaze
]

export const GIF_SERVICE = 'https://gifs.bsky.app'

export const GIF_KLIPY_SEARCH = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/search?${params}`
export const GIF_KLIPY_FEATURED = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/featured?${params}`

export const MAX_LABELERS = 20

export const VIDEO_SERVICE = 'https://video.bsky.app'
export const VIDEO_SERVICE_DID = 'did:web:video.bsky.app'

export const VIDEO_MAX_DURATION_MS = 3 * 60 * 1000 // 3 minutes in milliseconds
/**
 * Maximum size of a video in megabytes, _not_ mebibytes. Backend uses
 * ISO megabytes.
 */
export const VIDEO_MAX_SIZE_MB = 300
export const VIDEO_MAX_SIZE = VIDEO_MAX_SIZE_MB * 1000 * 1000 // 300mb

export const SUPPORTED_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/webm',
  'video/quicktime',
  'image/gif',
] as const

export type SupportedMimeTypes = (typeof SUPPORTED_MIME_TYPES)[number]

export const EMOJI_REACTION_LIMIT = 5

export const urls = {
  website: {
    blog: {
      findFriendsAnnouncement:
        'https://bsky.social/about/blog/12-16-2025-find-friends',
      initialVerificationAnnouncement: `https://bsky.social/about/blog/04-21-2025-verification`,
      searchTipsAndTricks: 'https://bsky.social/about/blog/05-31-2024-search',
    },
    support: {
      findFriendsPrivacyPolicy:
        'https://bsky.social/about/support/find-friends-privacy-policy',
    },
  },
}

export const PUBLIC_APPVIEW = PUBLIC_APP_URL
export const PUBLIC_APPVIEW_DID = 'did:web:api.bsky.app'
export const PUBLIC_STAGING_APPVIEW_DID = 'did:web:api.staging.bsky.dev'

export const DEV_ENV_APPVIEW = `http://localhost:2584` // always the same
export const DEV_ENV_APPVIEW_DID = `did:plc:dw4kbjf5mn7nhenabiqpkyh3` // always the same

// temp hack for e2e - esb
export const BLUESKY_PROXY_HEADER = {
  value: `${BLUESKY_PROXY_DID}#bsky_appview`,
  get() {
    return this.value as ProxyHeaderValue
  },
  set(value: string) {
    this.value = value
  },
}

export const CHAT_SERVICE = 'https://api.bsky.chat'

export const DM_SERVICE_HEADERS = {
  'atproto-proxy': `${CHAT_PROXY_DID}#bsky_chat`,
}

export const BLUESKY_MOD_SERVICE_HEADERS = {
  'atproto-proxy': `${APP_LABELER_DID}#atproto_labeler`,
}

export const BLUESKY_NOTIF_SERVICE_HEADERS = {
  'atproto-proxy': `${BLUESKY_PROXY_DID}#bsky_notif`,
}

export const webLinks = {
  tos: `https://bsky.social/about/support/tos`,
  privacy: `https://bsky.social/about/support/privacy-policy`,
  community: `https://bsky.social/about/support/community-guidelines`,
  communityDeprecated: `https://bsky.social/about/support/community-guidelines-deprecated`,
}

export const THEME_ONLY_DARK = process.env.THEME_ONLY_DARK === 'true'
export const DEV_OVERLAY = !!process.env.DEV_OVERLAY
export const ONE_MINUTE = 60_000
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24
export const ONE_WEEK = ONE_DAY * 7
export const ONE_MONTH = ONE_DAY * 30
export const ONE_YEAR = ONE_MONTH * 12

export const WSOCIAL_LABELER_DID = APP_LABELER_DID // W Social Ozone DID
export const WSOCIAL_ACCOUNT_TYPE_LABELS = [
  'wsocial-personal',
  'wsocial-org',
  'wsocial-bot',
  'wsocial-service',
  'wsocial-test',
] as const

export const WSOCIAL_APPVIEW_URL =
  process.env.EXPO_PUBLIC_WSOCIAL_APPVIEW_URL ?? 'https://appview.wsocial.eu'

export const WSOCIAL_APPVIEW = WSOCIAL_APPVIEW_URL

export const WIDENTITY_DEEP_LINK = 'neuroaccess://'
